import { z, type output } from 'zod'
import type { LocationType } from './eventTypes'

export const createGeneralInfoSchema = (messages: {
  titleMin: string
  contentRequired: string
  startDateRequired: string
  endBeforeStart?: string
}) =>
  z
    .object({
      isGroupEvent: z.boolean(),
      title: z.string().trim().min(3, messages.titleMin),
      intro: z.string().trim().optional(),
      content: z.string().trim().min(1, messages.contentRequired),
      start: z.string().min(1, messages.startDateRequired),
      end: z.string().optional(),
    })
    .refine(
      (data) => {
        if (!data.end) return true
        return new Date(data.end).getTime() >= new Date(data.start).getTime()
      },
      {
        message: messages.endBeforeStart || 'End date must be after start date',
        path: ['end'],
      },
    )

export type GeneralInfoFormValues = output<
  ReturnType<typeof createGeneralInfoSchema>
>

export const createLocationCoverSchema = (
  locationType: LocationType,
  messages: {
    addressRequired: string
    linkInvalid: string
  },
) =>
  z.object({
    address:
      locationType === 'offline'
        ? z.string().trim().min(1, messages.addressRequired)
        : z.string().optional().default(''),
    link:
      locationType === 'online'
        ? z.string().url(messages.linkInvalid)
        : z.string().optional().default(''),
    geo: z.object({ lat: z.number(), long: z.number() }).optional(),
  })

export type LocationCoverFormValues = output<
  ReturnType<typeof createLocationCoverSchema>
>

export const createEventSettingsSchema = (messages: {
  limitMin: string
  visibilityRequired: string
}) =>
  z.object({
    limit: z.number({ error: messages.limitMin }).min(1, messages.limitMin).optional(),
    enableWaitlist: z.boolean(),
    visibility: z
      .array(z.enum(['followers', 'members', 'public']))
      .min(1, messages.visibilityRequired),
    reminder: z.enum(['none', '1day', '1week']),
  })

export type EventSettingsFormValues = output<
  ReturnType<typeof createEventSettingsSchema>
>
